import type {ReactNode, ComponentType} from 'react'
import {Children} from 'react'
import {faCaretUp, faCaretDown} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import assign from 'lodash/assign'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import last from 'lodash/last'
import type {
  DropdownIndicatorProps,
  OptionProps,
  ValueContainerProps,
  MenuProps,
  CSSObjectWithLabel,
  GroupBase,
  ActionMeta,
  FormatOptionLabelMeta
} from 'react-select'
import Select, {components} from 'react-select'
import {exhaustive} from '@freckle/exhaustive'
import {mmap} from '@freckle/maybe'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Label} from '@freckle/educator-materials/ts/components/label'
import {TriStateCheckbox} from '@freckle/educator-materials/ts/components/tri-state-checkbox'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {DropdownOptionsT, OptionType} from '@freckle/educator-materials/ts/logic/dropdown'
import {classPrefix} from './class-prefix'
import {
  customDropdownStyles,
  dropdownIndicatorStyle,
  labelStyle,
  horizontalLabelStyle,
  dropdownFlex,
  menuButtons
} from './dropdown.module.scss'

export function DropdownIndicator<T>(props: DropdownIndicatorProps<T, boolean, GroupBase<T>>) {
  const TypedDropdownIndicator = components.DropdownIndicator as ComponentType<
    DropdownIndicatorProps<T, boolean, GroupBase<T>>
  >
  return (
    <TypedDropdownIndicator {...props}>
      <span data-test="dropdown-indicator" className={dropdownIndicatorStyle}>
        <FontAwesomeIcon icon={props.selectProps.menuIsOpen ? faCaretUp : faCaretDown} />
      </span>
    </TypedDropdownIndicator>
  )
}

function Option<T = unknown>(props: OptionProps<T>) {
  const TypedOption = components.Option as ComponentType<OptionProps<T>>
  return (
    <div>
      <TypedOption {...props}>
        <TriStateCheckbox
          id={props.label}
          label={props.label}
          value={(props as unknown as {value: T}).value}
          state={props.isSelected ? 'selected' : 'unselected'}
          onSelected={() => {}}
        />
      </TypedOption>
    </div>
  )
}

function CustomValueContainer<T>(props: ValueContainerProps<T>) {
  const {children, ...restProps} = props
  const {getValue, selectProps} = restProps
  const {optionsWithoutGroupings, allSelectedText, noneSelectedText, formatSelectionText} =
    selectProps as unknown as {
      optionsWithoutGroupings: unknown[]
      allSelectedText?: string
      noneSelectedText?: string
      formatSelectionText?: (numSelected: number) => string | ReactNode
    }
  const {t} = useSafeTranslation('common')
  const currentValues = getValue()
  const isAllSelected = optionsWithoutGroupings.length === currentValues.length
  const isNoneSelected = currentValues.length === 0
  const newValue = isAllSelected
    ? (allSelectedText ?? t('DROPDOWN_MULTI_ALL_SELECTED_LABEL'))
    : isNoneSelected
      ? (noneSelectedText ??
        t('DROPDOWN_MULTI_SOME_SELECTED_LABEL', {
          numSelected: currentValues.length
        }))
      : formatSelectionText
        ? formatSelectionText(currentValues.length)
        : t('DROPDOWN_MULTI_SOME_SELECTED_LABEL', {
            numSelected: currentValues.length
          })

  const newChildren = last(Children.toArray(children))
  const TypedValueContainer = components.ValueContainer as ComponentType<ValueContainerProps<T>>
  return (
    <TypedValueContainer {...restProps}>
      {newValue} {newChildren}
    </TypedValueContainer>
  )
}

function Menu<T>(props: MenuProps<T>) {
  const {children, hasValue, selectProps} = props
  const {onChange} = selectProps
  const {t} = useSafeTranslation('common')
  const TypedMenu = components.Menu as ComponentType<MenuProps<T>>
  return (
    <TypedMenu {...props}>
      <div>{children}</div>
      <div data-test="select-clear-all" className={menuButtons}>
        {hasValue ? (
          // @ts-ignore https://app.asana.com/0/1203006199529183/1205252238105839/f
          <Button onClick={() => onChange([])} style="link">
            {t('DROPDOWN_MULTI_CLEAR_ALL_LABEL')}
          </Button>
        ) : (
          <Button
            // @ts-ignore https://app.asana.com/0/1203006199529183/1205252238105839/f
            onClick={() => onChange(selectProps.optionsWithoutGroupings)}
            style="link"
          >
            {t('DROPDOWN_MULTI_SELECT_ALL_LABEL')}
          </Button>
        )}
      </div>
    </TypedMenu>
  )
}

type CommonProps<T> = {
  id?: string
  options: DropdownOptionsT<T>
  isDisabled?: boolean
  placeholder?: string
  isClearable?: boolean
  isSearchable?: boolean
  ariaLabelledby?: string
  dataTest?: string
  labelText?: string
  labelClass?: string
  tooltip?: ReactNode
  dropdownClass?: string
  horizontal?: boolean
  allSelectedText?: string
  noneSelectedText?: string
  formatSelectionText?: (numSelected: number) => string | ReactNode
  noOptionsMessage?: () => string
  portalTarget?: HTMLElement | null // if you have trouble with a react-table, set this to document.body
  formatOptionLabel?: (
    data: OptionType<T>,
    formatOptionLabelMeta: FormatOptionLabelMeta<OptionType<T>>
  ) => ReactNode
}

type MultiSelectProps<T> = CommonProps<T> & {
  value: ReadonlyArray<OptionType<T>>
  isMultiSelect: true
  handleChange: (a: ReadonlyArray<OptionType<T>>, actionMeta?: ActionMeta<OptionType<T>>) => void
}

type SingleSelectProps<T> = CommonProps<T> & {
  value: OptionType<T> | null
  isMultiSelect?: undefined
  handleChange: (a: OptionType<T> | null, actionMeta?: ActionMeta<OptionType<T>>) => void
}

export type Props<T> = MultiSelectProps<T> | SingleSelectProps<T>

function Dropdown<T>(props: Props<T>) {
  const {
    id,
    placeholder,
    isDisabled = false,
    value,
    handleChange,
    options,
    isMultiSelect,
    isClearable = false,
    ariaLabelledby,
    isSearchable,
    dataTest = 'custom-select-dropdown',
    labelText,
    labelClass,
    tooltip,
    allSelectedText,
    formatSelectionText,
    noneSelectedText,
    noOptionsMessage,
    dropdownClass,
    horizontal = false,
    portalTarget,
    formatOptionLabel
  } = props

  let optionsWithoutGroupings,
    untaggedOptions = []
  switch (options.tag) {
    case 'flat':
      untaggedOptions = options.options
      optionsWithoutGroupings = options.options
      break
    case 'grouped':
      untaggedOptions = options.groups
      optionsWithoutGroupings = options.groups.flatMap(({options}) => options)
      break
    default:
      exhaustive((options as {tag: never}).tag, 'DropdownOptionsT')
  }

  const customComponents = isMultiSelect
    ? {
        DropdownIndicator,
        Option,
        ValueContainer: CustomValueContainer,
        Menu
      }
    : {DropdownIndicator}

  function isOptionSelected(option: OptionType<T>): boolean {
    switch (isMultiSelect) {
      case true:
        return value.some(({value}) => isEqual(value, option.value))
      case undefined:
        return value !== null && isEqual(value.value, option.value)
      default:
        return exhaustive(isMultiSelect)
    }
  }

  const customStyles = {
    control: (provided: CSSObjectWithLabel) =>
      assign(provided, {
        minHeight: '33.5px',
        height: '33.5px'
      }),
    valueContainer: (provided: CSSObjectWithLabel) =>
      assign(provided, {
        height: '33.5px',
        padding: '0 10px'
      }),
    input: (provided: CSSObjectWithLabel) =>
      assign(provided, {
        margin: '0px'
      }),
    indicatorSeparator: (_state: CSSObjectWithLabel) => ({
      display: 'none'
    }),
    indicatorsContainer: (provided: CSSObjectWithLabel) =>
      assign(provided, {
        height: '33.5px',
        display: 'flex',
        alignItems: 'center'
      }),
    menu: (base: CSSObjectWithLabel) =>
      assign(base, {
        width: 'max-content',
        minWidth: '100%',
        zIndex: 1000
      }),
    option: (styles: CSSObjectWithLabel) =>
      assign(styles, {
        paddingRight: 36,
        paddingTop: 4,
        paddingBottom: 4
      })
  }

  return (
    <div data-test={dataTest} className={horizontal ? dropdownFlex : undefined}>
      {mmap(
        text =>
          isEmpty(text) ? null : (
            <Label
              className={addMaybeClassName(
                horizontal ? horizontalLabelStyle : labelStyle,
                labelClass
              )}
            >
              {text} {tooltip}
            </Label>
          ),
        labelText
      )}
      <Select
        id={id}
        classNamePrefix={classPrefix}
        components={customComponents}
        placeholder={placeholder}
        value={value}
        onChange={(
          currentValue: ReadonlyArray<OptionType<T>> | OptionType<T> | null,
          actionMeta: ActionMeta<OptionType<T>>
        ) => {
          switch (isMultiSelect) {
            case true:
              ;(handleChange as MultiSelectProps<T>['handleChange'])(
                (currentValue ?? []) as ReadonlyArray<OptionType<T>>,
                actionMeta
              )
              break
            case undefined:
              ;(handleChange as SingleSelectProps<T>['handleChange'])(
                currentValue as OptionType<T> | null,
                actionMeta
              )
              break
            default:
              exhaustive(isMultiSelect)
          }
        }}
        options={untaggedOptions}
        // @ts-ignore typescript tells me the property below shouldn't exist.
        optionsWithoutGroupings={optionsWithoutGroupings}
        isClearable={isClearable}
        className={addMaybeClassName(customDropdownStyles, dropdownClass)}
        isDisabled={isDisabled}
        aria-labelledby={ariaLabelledby}
        isSearchable={isSearchable}
        isMulti={isMultiSelect === true}
        closeMenuOnSelect={!isMultiSelect}
        hideSelectedOptions={false}
        isOptionSelected={isOptionSelected}
        styles={customStyles}
        allSelectedText={allSelectedText}
        formatSelectionText={formatSelectionText}
        noneSelectedText={noneSelectedText}
        noOptionsMessage={noOptionsMessage}
        menuPortalTarget={portalTarget}
        formatOptionLabel={formatOptionLabel}
      />
    </div>
  )
}

export {Dropdown}
